/* eslint-disable */
import { useState, useEffect } from "react";
import { getDatabase, ref, get, onValue } from "firebase/database";
import CryptoJS from "crypto-js";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { 
  BarChart3, 
  Zap, 
  Activity, 
  TrendingUp,
  Calendar,
  ChevronDown,
  X
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Dashboard.css";
import ModelUsageRechart from "./ModelUsageRechart";
const Dashboard = () => {
  const [adminName1, setAdminName1] = useState("");
  const [timeFilter, setTimeFilter] = useState("all"); // all, today, yesterday, week, thisMonth, pastMonth, custom
  const [aiGraphFilter, setAiGraphFilter] = useState("all"); // AI graph syncs with main dashboard filter
  const [loading, setLoading] = useState(true);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(new Date());
  const [customEndDate, setCustomEndDate] = useState(new Date());
  const [dateRangeError, setDateRangeError] = useState("");
  const [aiCustomStartDate, setAiCustomStartDate] = useState(new Date());
  const [aiCustomEndDate, setAiCustomEndDate] = useState(new Date());
  const [showAiDatePicker, setShowAiDatePicker] = useState(false);

  // Decryption function for patient names
  const decryptPatientName = (encryptedData) => {
    try {
      if (!encryptedData || typeof encryptedData !== "string") {
        return "Unknown Patient";
      }
      
      // Check if data starts with encryption prefix
      if (encryptedData.startsWith("U2FsdGVkX1")) {
        const secretKey = process.env.REACT_APP_SECRET_KEY;
        const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        return decrypted || "Unknown Patient";
      }
      
      return encryptedData; // Return as-is if not encrypted
    } catch (error) {
      console.warn("Error decrypting patient name:", error);
      return "Unknown Patient";
    }
  };

  // Statistics state
  const [stats, setStats] = useState({
    totalUploads: 0,
    ctUploads: 0,
    xrayUploads: 0,
    mriUploads: 0,
    todayUploads: 0,
    yesterdayUploads: 0,
    weekUploads: 0,
    monthUploads: 0,
    totalPatients: 0,
    totalDoctors: 0,
    totalTechnicians: 0,
  });

  const [reportStats, setReportStats] = useState({
    total: 0,
    final: 0,
    read: 0,
    pending: 0,
  });

  const [aiStats, setAIStats] = useState({
    totalRuns: 0,
    byModel: {},
    recentRuns: [],
  });

  const [filteredAiStats, setFilteredAiStats] = useState({
    totalRuns: 0,
    byModel: {},
    recentRuns: [],
  });

  const [uploadTrends, setUploadTrends] = useState([]);
  const [modalityBreakdown, setModalityBreakdown] = useState([]);
  const [aiUsageTrends, setAiUsageTrends] = useState([]);
  const [reportsTableData, setReportsTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalReportsCount, setTotalReportsCount] = useState(0);
  const [loadingReports, setLoadingReports] = useState(false);
  const [studyCategoryData, setStudyCategoryData] = useState([]);
  
  // Separate state for actual week/month uploads (independent of filter)
  const [actualWeekUploads, setActualWeekUploads] = useState(0);
  const [actualMonthUploads, setActualMonthUploads] = useState(0);


  const senderEmail = localStorage.getItem("senderEmail");
  const showMriCt = senderEmail === "rohit.yadav@gmail.com";

  useEffect(() => {
    const adminName = localStorage.getItem("adminName");
    setAdminName1(adminName);
    if (adminName) {
      fetchDashboardData(adminName);
    }
  }, []);

  // Fetch reports with batch fetching when page/filters change
  useEffect(() => {
    if (adminName1) {
      fetchReportsBatch();
    }
  }, [currentPage, itemsPerPage, timeFilter, adminName1]);

  // Auto-apply custom date filter when both dates are selected
  useEffect(() => {
    if (timeFilter === "custom" && customStartDate && customEndDate && adminName1) {
      // Trigger filter update immediately when both dates are set
      handleFilterChange("custom");
    }
  }, [customStartDate, customEndDate, timeFilter, adminName1]);

  // Fetch reports with batch fetching (server-side pagination)
  const fetchReportsBatch = async () => {
    setLoadingReports(true);
    const db = getDatabase();
    
    try {
      const patientsRef = ref(db, `superadmin/admins/${adminName1}/patients`);
      const patientsSnapshot = await get(patientsRef);
      const patientsData = patientsSnapshot.val() || {};
      
      // Process all data with time filter to get doctor report counts
      const allReports = processReportsTableData(patientsData, timeFilter);
      setTotalReportsCount(allReports.length);
      
      // Calculate pagination
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedReports = allReports.slice(startIndex, endIndex);
      
      setReportsTableData(paginatedReports);
      setLoadingReports(false);
    } catch (error) {
      console.error("Error fetching reports batch:", error);
      setLoadingReports(false);
    }
  };

  const fetchDashboardData = async (adminName) => {
    setLoading(true);
    const db = getDatabase();

    try {
      // Fetch modality counts
      const costingRef = ref(db, `superadmin/admins/${adminName}/costing`);
      const costingSnapshot = await get(costingRef);
      const costingData = costingSnapshot.val() || {};

      // Fetch patients data
      const patientsRef = ref(db, `superadmin/admins/${adminName}/patients`);
      const patientsSnapshot = await get(patientsRef);
      const patientsData = patientsSnapshot.val() || {};

      // Fetch doctors and technicians
      const doctorsRef = ref(db, `superadmin/admins/${adminName}/doctors`);
      const doctorsSnapshot = await get(doctorsRef);
      const doctorsData = doctorsSnapshot.val() || {};

      const techniciansRef = ref(db, `superadmin/admins/${adminName}/technicians`);
      const techniciansSnapshot = await get(techniciansRef);
      const techniciansData = techniciansSnapshot.val() || {};

      // Fetch Postpaid data for AI statistics
      const postpaidRef = ref(db, `superadmin/admins/${adminName}/Postpaid`);
      const postpaidSnapshot = await get(postpaidRef);
      const postpaidData = postpaidSnapshot.val() || {};

      // Process upload events for time-based statistics
      const uploadEvents = costingData.Upload_Events || {};
      const timeBasedStats = processUploadEvents(uploadEvents);

      // Use getFilteredStatistics for consistent data processing (including AI stats)
      const initialStats = getFilteredStatistics(uploadEvents, patientsData, "all", costingData, postpaidData);
      
      // Process upload trends
      const trends = processUploadTrends(uploadEvents);

      // Update all states using filtered statistics
      setStats({
        totalUploads: initialStats.stats.totalUploads,
        ctUploads: initialStats.modalityBreakdown.find(m => m.name === "CT")?.value || 0,
        xrayUploads: initialStats.modalityBreakdown.find(m => m.name === "X-ray")?.value || 0,
        mriUploads: initialStats.modalityBreakdown.find(m => m.name === "MRI")?.value || 0,
        todayUploads: timeBasedStats.today,
        yesterdayUploads: timeBasedStats.yesterday,
        weekUploads: timeBasedStats.week,
        monthUploads: timeBasedStats.month,
        totalPatients: Object.keys(patientsData).length,
        totalDoctors: Object.keys(doctorsData).length,
        totalTechnicians: Object.keys(techniciansData).length,
      });

      setReportStats(initialStats.reportStats);
      setAIStats(initialStats.aiStats);
      setFilteredAiStats(initialStats.aiStats); // Initialize filtered AI stats
      setModalityBreakdown(initialStats.modalityBreakdown);
      setUploadTrends(trends);
      
      // Process AI usage trends (starts with "all" to match initial filter)
      const aiTrends = processAIUsageTrends(postpaidData, "all");
      setAiUsageTrends(aiTrends);
      
      // Set actual week and month uploads (independent of filter)
      setActualWeekUploads(timeBasedStats.week);
      setActualMonthUploads(timeBasedStats.month);

      // Reports table data is now handled by fetchReportsBatch separately

      // Process study category data
      const studyCategories = processStudyCategoryData(patientsData, "all");
      setStudyCategoryData(studyCategories);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setLoading(false);
    }
  };

  // Map DICOM modality codes to standard categories (XRAY, CT, MRI)
  const mapModality = (modality) => {
    if (!modality || modality === "N.A") return null;
    
    const modalityUpper = modality.toUpperCase().trim();
    
    // Map MRI variants
    if (modalityUpper === "MR" || 
        modalityUpper === "SR" || 
        modalityUpper === "MRS" || 
        modalityUpper === "MRA" || 
        modalityUpper === "MRV") {
      return "MRI";
    }
    
    // Map X-ray variants
    if (modalityUpper === "CR" || 
        modalityUpper === "DX" || 
        modalityUpper === "MG" || 
        modalityUpper === "XA" || 
        modalityUpper === "IO" || 
        modalityUpper === "BD" || 
        modalityUpper === "RF") {
      return "XRAY";
    }
    
    // Return as-is if already XRAY, CT, or MRI
    if (modalityUpper === "XRAY" || modalityUpper === "CT" || modalityUpper === "MRI") {
      return modalityUpper;
    }
    
    // Return null for unknown modalities
    return null;
  };

  // Process study category data - Categorize studies by body part type
  const processStudyCategoryData = (patientsData, filter = "all") => {
    const categoryCounts = {
      "Chest": 0,
      "Spine": 0,
      "Upper Extremity": 0,
      "Lower Extremity": 0,
      "Head and Neck": 0,
      "Abdomen": 0,
      "Mammography": 0,
      "Others": 0
    };

    // Mapping of study descriptions to categories based on the provided image
    const categoryMappings = {
      "chest pa": "Chest",
      "chest ap": "Chest",
      "cspine": "Spine",
      "lumbar spine": "Spine",
      "spine": "Spine",
      "cervical": "Spine",
      "hand": "Upper Extremity",
      "shoulder": "Upper Extremity",
      "wrist": "Upper Extremity",
      "arm": "Upper Extremity",
      "elbow": "Upper Extremity",
      "forearm": "Upper Extremity",
      "upper limb": "Upper Extremity",
      "pelvis": "Lower Extremity",
      "knee": "Lower Extremity",
      "foot": "Lower Extremity",
      "leg": "Lower Extremity",
      "lower limb": "Lower Extremity",
      "ankle": "Lower Extremity",
      "thigh": "Lower Extremity",
      "hip": "Lower Extremity",
      "nasopharinx": "Head and Neck",
      "nasopharynx": "Head and Neck",
      "skull": "Head and Neck",
      "mastoids": "Head and Neck",
      "mastoid": "Head and Neck",
      "pns": "Head and Neck",
      "abdomen": "Abdomen",
      "kub": "Abdomen",
      "abdomen/kub": "Abdomen",
      "mammography": "Mammography"
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate;
    let endDate = new Date();

    switch (filter) {
      case "today":
        startDate = today;
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 1);
        endDate = today;
        break;
      case "week":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "thisMonth":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "pastMonth":
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "custom":
        startDate = customStartDate;
        endDate = customEndDate;
        break;
      default:
        startDate = new Date(0);
    }

    Object.keys(patientsData).forEach((patientId) => {
      const patient = patientsData[patientId];

      // Check if patient matches the time filter
      if (patient.Timestamp) {
        const patientTimestamp = parseTimestamp(patient.Timestamp);
        let isInRange = false;

        if (filter === "all") {
          isInRange = true;
        } else if (filter === "today") {
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          isInRange = patientTimestamp >= startDate && patientTimestamp < tomorrow;
        } else if (filter === "yesterday") {
          isInRange = patientTimestamp >= startDate && patientTimestamp < endDate;
        } else if (filter === "custom") {
          const rangeStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const rangeEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);
          isInRange = patientTimestamp >= rangeStart && patientTimestamp <= rangeEnd;
        } else {
          if (filter === "pastMonth") {
            isInRange = patientTimestamp >= startDate && patientTimestamp <= endDate;
          } else {
            isInRange = patientTimestamp >= startDate;
          }
        }

        if (isInRange && patient.uploads) {
          // Get all scans with valid modalities (same logic as modality breakdown)
          const scansWithModality = [];
          
          Object.keys(patient.uploads).forEach((scanKey) => {
            const scan = patient.uploads[scanKey];
            const rawModality = scan.Modality || scan.modality || scan.MODALITY || scan.StudyType || "";
            const mappedModality = mapModality(rawModality);
            
            if (mappedModality) {
              scansWithModality.push({
                scanKey,
                scan,
                timestamp: scan.Timestamp || scan.timestamp || 0
              });
            }
          });
          
          // If patient has valid scans, use the latest one (same as modality breakdown)
          if (scansWithModality.length > 0) {
            // Sort by timestamp to get the latest scan
            scansWithModality.sort((a, b) => {
              const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
              const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
              return timeB - timeA; // Newest first
            });
            
            // Only process the LATEST scan for this patient
            const latestScan = scansWithModality[0].scan;
            
            // Collect all available study type fields (including NA values)
            const studyTypeFields = [];
            if (latestScan.Type_of_Data && latestScan.Type_of_Data.trim() !== "") {
              studyTypeFields.push(latestScan.Type_of_Data.trim().toLowerCase());
            }
            if (latestScan.Type_of_Dicom_Data && latestScan.Type_of_Dicom_Data.trim() !== "") {
              studyTypeFields.push(latestScan.Type_of_Dicom_Data.trim().toLowerCase());
            }
            if (latestScan.Type_of_CT && latestScan.Type_of_CT.trim() !== "") {
              studyTypeFields.push(latestScan.Type_of_CT.trim().toLowerCase());
            }

            // Try to categorize using any available field
            let matched = false;
            for (const studyType of studyTypeFields) {
              if (matched) break; // Already found a match
              
              // Check if value is NA or N.A - count as Others
              if (studyType === "na" || studyType === "n.a" || studyType === "n.a.") {
                categoryCounts["Others"]++;
                matched = true;
                break;
              }
              
              // Try direct match first
              if (categoryMappings[studyType]) {
                categoryCounts[categoryMappings[studyType]]++;
                matched = true;
                break;
              }
              
              // Try keyword search
              for (const [key, category] of Object.entries(categoryMappings)) {
                if (studyType.includes(key)) {
                  categoryCounts[category]++;
                  matched = true;
                  break;
                }
              }
            }
            
            // If no match found, count this patient's latest scan as Others
            if (!matched) {
              categoryCounts["Others"]++;
            }
          }
        }
      }
    });

    // Convert to array and filter out categories with zero count
    const categoryData = Object.keys(categoryCounts)
      .filter(category => categoryCounts[category] > 0)
      .map(category => ({
        category,
        count: categoryCounts[category]
      }));

    return categoryData;
  };

  // Process reports table data - Group by doctor and count Final reports only
  const processReportsTableData = (patientsData, filter = "all") => {
    const doctorReportCounts = {};
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate;
    let endDate = new Date();

    switch (filter) {
      case "today":
        startDate = today;
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 1);
        endDate = today;
        break;
      case "week":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "thisMonth":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "pastMonth":
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "custom":
        startDate = customStartDate;
        endDate = customEndDate;
        break;
      default:
        startDate = new Date(0);
    }
    
    Object.keys(patientsData).forEach((patientId) => {
      const patient = patientsData[patientId];
      const doctorName = patient.Report_Addedby || "Not Assigned";
      const reportStatus = (patient.Report_Status || "").toLowerCase();
      
      // Only count Final reports
      if (reportStatus !== "final" && reportStatus !== "finalized") {
        return;
      }
      
      // Check if patient has uploads with valid modalities (use mapModality function)
      let hasValidScans = false;
      if (patient.uploads) {
        Object.keys(patient.uploads).forEach((scanKey) => {
          const scan = patient.uploads[scanKey];
          const rawModality = scan.Modality || scan.modality || scan.MODALITY || scan.StudyType || "";
          const mappedModality = mapModality(rawModality);
          
          if (mappedModality) {
            hasValidScans = true;
          }
        });
      }
      
      // Only process patients with valid scans
      if (!hasValidScans) {
        return;
      }
      
      // Check if patient matches the time filter
      let isInRange = false;
      
      if (filter === "all") {
        // For "all" filter, include all patients regardless of Timestamp
        isInRange = true;
      } else if (patient.Timestamp) {
        // For other filters, only process patients with Timestamp
        const patientTimestamp = parseTimestamp(patient.Timestamp);
        
        if (filter === "today") {
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          isInRange = patientTimestamp >= startDate && patientTimestamp < tomorrow;
        } else if (filter === "yesterday") {
          isInRange = patientTimestamp >= startDate && patientTimestamp < endDate;
        } else if (filter === "custom") {
          const rangeStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const rangeEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);
          isInRange = patientTimestamp >= rangeStart && patientTimestamp <= rangeEnd;
        } else {
          if (filter === "pastMonth") {
            isInRange = patientTimestamp >= startDate && patientTimestamp <= endDate;
          } else {
            isInRange = patientTimestamp >= startDate;
          }
        }
      }
      
      // Count reports for each doctor
      if (isInRange) {
        if (!doctorReportCounts[doctorName]) {
          doctorReportCounts[doctorName] = 0;
        }
        doctorReportCounts[doctorName]++;
      }
    });
    
    // Convert to array, filter out "Not Assigned", and sort by count (highest first)
    const doctorReports = Object.keys(doctorReportCounts)
      .filter(doctorName => doctorName !== "Not Assigned")
      .map(doctorName => ({
        doctorName,
        reportCount: doctorReportCounts[doctorName]
      }));
    
    doctorReports.sort((a, b) => b.reportCount - a.reportCount);
    
    return doctorReports;
  };

  // Process upload events for time-based stats
  const processUploadEvents = (uploadEvents) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setDate(monthAgo.getDate() - 30);

    let todayCount = 0;
    let yesterdayCount = 0;
    let weekCount = 0;
    let monthCount = 0;

    Object.keys(uploadEvents).forEach((monthYear) => {
      const monthData = uploadEvents[monthYear];
      Object.keys(monthData).forEach((index) => {
        const event = monthData[index];
        if (event.Timestamp || event.timeStamp) {
          const timestamp = parseTimestamp(event.Timestamp || event.timeStamp);
          if (timestamp >= today) todayCount++;
          if (timestamp >= yesterday && timestamp < today) yesterdayCount++;
          if (timestamp >= weekAgo) weekCount++;
          if (timestamp >= monthAgo) monthCount++;
        }
      });
    });

    return {
      today: todayCount,
      yesterday: yesterdayCount,
      week: weekCount,
      month: monthCount,
    };
  };

  // Process report statistics
  const processReportStats = (patientsData) => {
    let total = 0;
    let final = 0;
    let read = 0;
    let pending = 0;

    Object.keys(patientsData).forEach((patientId) => {
      const patient = patientsData[patientId];
      total++;
      const status = (patient.Report_Status || "").toLowerCase();
      
      if (status === "final" || status === "finalized") {
        final++;
      } else if (status === "read") {
        read++;
      } else if (status === "pending" || status === "") {
        pending++;
      }
    });

    return { total, final, read, pending };
  };

  // Process AI statistics
  const processAIStats = (patientsData) => {
    let totalRuns = 0;
    const byModel = {};
    const recentRuns = [];

    Object.keys(patientsData).forEach((patientId) => {
      const patient = patientsData[patientId];
      
      // Check for AI results in different locations
      if (patient.uploads) {
        Object.keys(patient.uploads).forEach((scanKey) => {
          const scan = patient.uploads[scanKey];
          if (scan.AI_Report || scan.aiTasks) {
            totalRuns++;
            
            // Process AI tasks
            if (scan.aiTasks) {
              Object.keys(scan.aiTasks).forEach((taskKey) => {
                const task = scan.aiTasks[taskKey];
                const modelName = task.modelId || task.model || "Unknown";
                byModel[modelName] = (byModel[modelName] || 0) + 1;
                
                if (task.status && task.status.toLowerCase().includes("done")) {
                  recentRuns.push({
                    patientName: patient.FullName || "Unknown",
                    model: modelName,
                    status: task.status,
                    date: task.timestamp || new Date().toISOString(),
                  });
                }
              });
            }
            
            // Process AI Report
            if (scan.AI_Report) {
              const modelName = scan.AI_Report.model || "CXR Analysis";
              byModel[modelName] = (byModel[modelName] || 0) + 1;
            }
          }
        });
      }
      
      // Check legacy AI tasks
      if (patient.aiTasks) {
        Object.keys(patient.aiTasks).forEach((taskKey) => {
          const task = patient.aiTasks[taskKey];
          totalRuns++;
          const modelName = task.modelId || task.model || "Unknown";
          byModel[modelName] = (byModel[modelName] || 0) + 1;
        });
      }
    });

    // Sort recent runs by date
    recentRuns.sort((a, b) => new Date(b.date) - new Date(a.date));

    return {
      totalRuns,
      byModel,
      recentRuns: recentRuns.slice(0, 10), // Keep only 10 most recent
    };
  };

  // Process upload trends for line chart
  const processUploadTrends = (uploadEvents) => {
    const trends = [];
    const monthNames = [
      "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
      "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
    ];

    Object.keys(uploadEvents).forEach((monthYear) => {
      const monthData = uploadEvents[monthYear];
      const uploadCount = Object.keys(monthData).length;
      trends.push({
        month: monthYear,
        uploads: uploadCount,
      });
    });

    // Sort by date
    trends.sort((a, b) => {
      const [monthA, yearA] = a.month.split(" ");
      const [monthB, yearB] = b.month.split(" ");
      const dateA = new Date(yearA, monthNames.indexOf(monthA));
      const dateB = new Date(yearB, monthNames.indexOf(monthB));
      return dateA - dateB;
    });

    return trends.slice(-6); // Keep last 6 months
  };

  // Process AI usage trends over time
  const processAIUsageTrends = (postpaidData, filter, customStart = null, customEnd = null) => {
    const trends = {};
    const monthNames = [
      "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
      "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"
    ];

    // Set date ranges based on filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate;
    let endDate = new Date();

    switch (filter) {
      case "today":
        startDate = today;
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 1);
        endDate = today;
        break;
      case "week":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "thisMonth":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "pastMonth":
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "custom":
        startDate = customStart || aiCustomStartDate || customStartDate;
        endDate = customEnd || aiCustomEndDate || customEndDate;
        break;
      default:
        startDate = new Date(0);
    }

    // Determine grouping based on filter
    const getTimeKey = (timestamp, filterType) => {
      const date = parseTimestamp(timestamp);
      if (filterType === "today" || filterType === "yesterday") {
        return `${String(date.getHours()).padStart(2, '0')}:00`; // Hourly grouping
      } else if (filterType === "all") {
        return `${monthNames[date.getMonth()]} ${date.getFullYear()}`; // Monthly grouping for All Time
      } else {
        // Use daily grouping for week, thisMonth, pastMonth, custom
        return `${date.getDate()}/${date.getMonth() + 1}`; // Daily grouping
      }
    };

    if (postpaidData && Object.keys(postpaidData).length > 0) {
      Object.keys(postpaidData).forEach((monthKey) => {
        const monthData = postpaidData[monthKey];
        
        if (Array.isArray(monthData)) {
          monthData.forEach((aiRun) => {
            if (aiRun.AIModelName && aiRun.Timestamp) {
              const aiTimestamp = parseTimestamp(aiRun.Timestamp);
              
              // Filter by date range
              let isInRange = false;
              if (filter === "all") {
                isInRange = true;
              } else if (filter === "today") {
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                isInRange = aiTimestamp >= startDate && aiTimestamp < tomorrow;
              } else if (filter === "yesterday") {
                isInRange = aiTimestamp >= startDate && aiTimestamp < endDate;
              } else if (filter === "custom") {
                if (startDate && endDate) {
                  const rangeStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                  const rangeEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);
                  isInRange = aiTimestamp >= rangeStart && aiTimestamp <= rangeEnd;
                } else {
                  isInRange = false; // Skip if dates not selected
                }
              } else {
                if (filter === "pastMonth") {
                  isInRange = aiTimestamp >= startDate && aiTimestamp <= endDate;
                } else {
                  isInRange = aiTimestamp >= startDate;
                }
              }
              
              if (isInRange) {
                const timeKey = getTimeKey(aiRun.Timestamp, filter);
                const modelName = aiRun.AIModelName;
                
                if (!trends[timeKey]) {
                  trends[timeKey] = { time: timeKey };
                }
                
                trends[timeKey][modelName] = (trends[timeKey][modelName] || 0) + 1;
                trends[timeKey].total = (trends[timeKey].total || 0) + 1;
              }
            }
          });
        }
      });
    }

    // Convert to array and sort by time
    const trendsArray = Object.values(trends).sort((a, b) => {
      // Sort logic based on filter type
      if (filter === "today" || filter === "yesterday") {
        // Hourly sorting (format: "09:00")
        const hourA = parseInt(a.time.split(':')[0]);
        const hourB = parseInt(b.time.split(':')[0]);
        return hourA - hourB;
      } else if (filter === "all") {
        // Monthly sorting for All Time (format: "OCT 2025")
        const [monthA, yearA] = a.time.split(" ");
        const [monthB, yearB] = b.time.split(" ");
        const dateA = new Date(yearA, monthNames.indexOf(monthA));
        const dateB = new Date(yearB, monthNames.indexOf(monthB));
        return dateA - dateB;
      } else {
        // Daily sorting for week, thisMonth, pastMonth, custom (format: "30/10")
        const [dayA, monthA] = a.time.split('/').map(Number);
        const [dayB, monthB] = b.time.split('/').map(Number);
        return (monthA * 100 + dayA) - (monthB * 100 + dayB);
      }
    });

    return trendsArray;
  };

  // Process AI statistics from Postpaid data
  const processPostpaidAIStats = (postpaidData) => {
    let totalRuns = 0;
    const byModel = {};
    const recentRuns = [];

    if (postpaidData && Object.keys(postpaidData).length > 0) {
      
      // Iterate through each month (OCT 2025, SEP 2025, etc.)
      Object.keys(postpaidData).forEach((monthKey) => {
        const monthData = postpaidData[monthKey];
        
        // Check if monthData is an array
        if (Array.isArray(monthData)) {
          monthData.forEach((aiRun, index) => {
            // Check if AI run has required fields
            if (aiRun.AIModelName && aiRun.Timestamp) {
              totalRuns++;
              const modelName = aiRun.AIModelName || "Unknown";
              byModel[modelName] = (byModel[modelName] || 0) + 1;
              
              // Add to recent runs
              recentRuns.push({
                id: `${monthKey}-${index}`,
                model: modelName,
                patientName: decryptPatientName(aiRun.PatientName),
                date: aiRun.Timestamp,
                status: aiRun.Status || "Completed"
              });
            }
          });
        }
      });
      
      // Sort recent runs by timestamp (newest first)
      recentRuns.sort((a, b) => {
        const timeA = parseTimestamp(a.date);
        const timeB = parseTimestamp(b.date);
        return timeB - timeA;
      });
    }

    return {
      totalRuns,
      byModel,
      recentRuns: recentRuns.slice(0, 10), // Keep only 10 most recent
    };
  };

  // Get filtered modality data for pie chart
  const getFilteredModalityData = (uploadEvents, timeFilter, costingData) => {
    const modalityData = [
      { name: "X-ray", value: costingData.XrayUploads || 0, color: "#0088FE" },
      // { name: "CT", value: costingData.CTUploads || 0, color: "#00C49F" },
      // { name: "MRI", value: costingData.MriUploads || 0, color: "#FFBB28" },
    ].filter((item) => item.value > 0);

    // Filter by time period
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate;

    switch (filter) {
      case "today":
        startDate = today;
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 1);
        break;
      case "week":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "month":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        break;
      default:
        startDate = new Date(0);
    }

    const counts = { XRAY: 0, CT: 0, MRI: 0 };

    Object.keys(uploadEvents).forEach((monthYear) => {
      const monthData = uploadEvents[monthYear];
      Object.keys(monthData).forEach((index) => {
        const event = monthData[index];
        const timestamp = parseTimestamp(event.Timestamp || event.timeStamp);
        const mappedModality = mapModality((event.Modality || "").toUpperCase());

        if (filter === "yesterday") {
          const endDate = new Date(today);
          if (timestamp >= startDate && timestamp < endDate) {
            if (mappedModality && counts.hasOwnProperty(mappedModality)) {
              counts[mappedModality]++;
            }
          }
        } else if (timestamp >= startDate) {
          if (mappedModality && counts.hasOwnProperty(mappedModality)) {
            counts[mappedModality]++;
          }
        }
      });
    });

    return [
      { name: "X-ray", value: counts.XRAY, color: "#0088FE" },
      { name: "CT",    value: counts.CT,   color: "#00C49F" },
      { name: "MRI",   value: counts.MRI,  color: "#FFBB28" },
    ].filter((item) => item.value > 0);
  };

  // Parse timestamp from various formats
  const parseTimestamp = (timestamp) => {
    if (!timestamp) return new Date(0);
    
    // Format: "DD/MM/YYYY, HH:MM:SS am/pm" or "DD/MM/YYYY, HH:MM:SS"
    const parts = timestamp.split(", ");
    if (parts.length === 2) {
      const [datePart, timePart] = parts;
      const [day, month, year] = datePart.split("/");
      
      // Handle am/pm format
      let timeString = timePart;
      let hours, minutes, seconds = 0;
      
      if (timePart.includes(" am") || timePart.includes(" pm")) {
        // Format: "09:59:49 am" or "02:30:15 pm"
        const isPM = timePart.includes(" pm");
        const cleanTime = timePart.replace(" am", "").replace(" pm", "");
        const timeParts = cleanTime.split(":");
        
        hours = parseInt(timeParts[0]);
        minutes = parseInt(timeParts[1]);
        seconds = timeParts[2] ? parseInt(timeParts[2]) : 0;
        
        // Convert to 24-hour format
        if (isPM && hours !== 12) {
          hours += 12;
        } else if (!isPM && hours === 12) {
          hours = 0;
        }
      } else {
        // Format: "09:59:49" (24-hour format)
        const timeParts = timePart.split(":");
        hours = parseInt(timeParts[0]);
        minutes = parseInt(timeParts[1]);
        seconds = timeParts[2] ? parseInt(timeParts[2]) : 0;
      }
      
      const parsedDate = new Date(year, month - 1, day, hours, minutes, seconds);
      return parsedDate;
    }
    
    return new Date(timestamp);
  };

  // Handle time filter change - Recalculate all statistics based on selected time period
  const handleFilterChange = async (filter) => {
    setTimeFilter(filter);
    setAiGraphFilter(filter); // Sync AI graph filter with main filter
    
    setCurrentPage(1); // Reset to first page
    if (adminName1) {
      // Show loading during tab switch
      setLoading(true);
      
      try {
        const db = getDatabase();
        
        // Fetch all data again
        const costingRef = ref(db, `superadmin/admins/${adminName1}/costing`);
        const costingSnapshot = await get(costingRef);
        const costingData = costingSnapshot.val() || {};
        
        const patientsRef = ref(db, `superadmin/admins/${adminName1}/patients`);
        const patientsSnapshot = await get(patientsRef);
        const patientsData = patientsSnapshot.val() || {};
        
        // Fetch Postpaid data for AI runs
        const postpaidRef = ref(db, `superadmin/admins/${adminName1}/Postpaid`);
        const postpaidSnapshot = await get(postpaidRef);
        const postpaidData = postpaidSnapshot.val() || {};
        
        const uploadEvents = costingData.Upload_Events || {};
        
        // Apply time filter to all statistics
        const filteredStats = getFilteredStatistics(uploadEvents, patientsData, filter, costingData, postpaidData);
        
        // Calculate actual week and month uploads (independent of filter)
        const weekStats = getFilteredStatistics(uploadEvents, patientsData, "week", costingData);
        const monthStats = getFilteredStatistics(uploadEvents, patientsData, "thisMonth", costingData);
        
        // Update all states with filtered data
        setStats(filteredStats.stats);
        setReportStats(filteredStats.reportStats);
        setAIStats(filteredStats.aiStats);
        setFilteredAiStats(filteredStats.aiStats);
        setModalityBreakdown(filteredStats.modalityBreakdown);
        setActualWeekUploads(weekStats.stats.totalUploads);
        setActualMonthUploads(monthStats.stats.totalUploads);
        
        // Update study category data
        const studyCategories = processStudyCategoryData(patientsData, filter);
        setStudyCategoryData(studyCategories);
        
        // Update AI usage trends with the selected filter
        const aiTrends = processAIUsageTrends(
          postpaidData, 
          filter, 
          filter === "custom" ? customStartDate : null, 
          filter === "custom" ? customEndDate : null
        );
        setAiUsageTrends(aiTrends);
        
        // Update Report Summary table based on filter
        const allReports = processReportsTableData(patientsData, filter);
        setTotalReportsCount(allReports.length);
        
        // Calculate pagination for reports
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedReports = allReports.slice(startIndex, endIndex);
        setReportsTableData(paginatedReports);
        
        // Hide loading after processing
        setLoading(false);
      } catch (error) {
        console.error("Error filtering dashboard data:", error);
        setLoading(false);
      }
    }
  };

  // Handle AI graph filter change - Only filter AI statistics
  const handleAiGraphFilterChange = async (filter) => {
    setAiGraphFilter(filter);
    if (adminName1) {
      try {
        const db = getDatabase();
        
        // Fetch Postpaid data for AI runs
        const postpaidRef = ref(db, `superadmin/admins/${adminName1}/Postpaid`);
        const postpaidSnapshot = await get(postpaidRef);
        const postpaidData = postpaidSnapshot.val() || {};
        
        const patientsRef = ref(db, `superadmin/admins/${adminName1}/patients`);
        const patientsSnapshot = await get(patientsRef);
        const patientsData = patientsSnapshot.val() || {};
        
        // Use aiCustomStartDate and aiCustomEndDate for custom filter
        const startDate = filter === "custom" ? aiCustomStartDate : customStartDate;
        const endDate = filter === "custom" ? aiCustomEndDate : customEndDate;
        
        // Filter only AI stats
        const filteredStats = getFilteredStatistics({}, patientsData, filter, {}, postpaidData);
        
        // Calculate AI usage trends over time
        const aiTrends = processAIUsageTrends(
          postpaidData, 
          filter, 
          filter === "custom" ? aiCustomStartDate : null, 
          filter === "custom" ? aiCustomEndDate : null
        );
        
        // Update only filtered AI stats and trends
        setFilteredAiStats(filteredStats.aiStats);
        setAiUsageTrends(aiTrends);
      } catch (error) {
        console.error("Error filtering AI graph data:", error);
      }
    }
  };

  // Get filtered statistics based on time filter
  const getFilteredStatistics = (uploadEvents, patientsData, filter, costingData, postpaidData = {}) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate;
    let endDate = new Date();

    switch (filter) {
      case "today":
        startDate = today;
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 1);
        endDate = today;
        break;
      case "week":
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 7);
        break;
      case "thisMonth":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "pastMonth":
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "custom":
        startDate = customStartDate;
        endDate = customEndDate;
        break;
      default:
        startDate = new Date(0);
    }
    
    // Filter patients by their Timestamp, then count their scans' modalities
    let totalUploads = 0;
    const modalityCounts = { XRAY: 0, CT: 0, MRI: 0 };
    const filteredPatientIds = new Set();
    
    // Iterate through all patients
    Object.keys(patientsData).forEach((patientId) => {
      const patient = patientsData[patientId];
      let isPatientInRange = false;
      
      // Check if patient matches the time filter based on patient's Timestamp
      if (filter === "all") {
        isPatientInRange = true;
      } else if (patient.Timestamp) {
        const patientTimestamp = parseTimestamp(patient.Timestamp);
        
        if (filter === "today") {
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          isPatientInRange = patientTimestamp >= startDate && patientTimestamp < tomorrow;
        } else if (filter === "yesterday") {
          isPatientInRange = patientTimestamp >= startDate && patientTimestamp < endDate;
        } else if (filter === "custom") {
          // Custom date range - include both start and end dates
          const rangeStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const rangeEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);
          isPatientInRange = patientTimestamp >= rangeStart && patientTimestamp <= rangeEnd;
        } else {
          // week, thisMonth, or pastMonth
          if (filter === "pastMonth") {
            isPatientInRange = patientTimestamp >= startDate && patientTimestamp <= endDate;
          } else {
            isPatientInRange = patientTimestamp >= startDate;
          }
        }
      }
      
      // If patient is in range, count based on their latest scan's modality
      if (isPatientInRange && patient.uploads) {
        // Get all scans with valid modalities
        const scansWithModality = [];
        
        Object.keys(patient.uploads).forEach((scanKey) => {
          const scan = patient.uploads[scanKey];
          const rawModality = scan.Modality || scan.modality || scan.MODALITY || scan.StudyType || "";
          const mappedModality = mapModality(rawModality);
          
          if (mappedModality) {
            scansWithModality.push({
              scanKey,
              modality: mappedModality,
              timestamp: scan.Timestamp || scan.timestamp || 0
            });
          }
        });
        
        // If patient has valid scans, use the latest one
        if (scansWithModality.length > 0) {
          // Sort by timestamp to get the latest scan
          scansWithModality.sort((a, b) => {
            const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return timeB - timeA; // Newest first
          });
          
          // Count patient once based on their latest scan's modality
          const latestModality = scansWithModality[0].modality;
          totalUploads++;
          modalityCounts[latestModality]++;
          filteredPatientIds.add(patientId);
        }
      }
    });
    
    // Filter reports and AI stats based on timestamps
    let filteredReportStats = { total: 0, final: 0, read: 0, pending: 0 };
    let filteredAIStats = { totalRuns: 0, byModel: {}, recentRuns: [] };
    
    // Process Report Stats - Only count patients who have valid scans
    if (filter === "all") {
      // For "all" filter, only count patients who have scans with valid modalities
      Object.keys(patientsData).forEach((patientId) => {
        const patient = patientsData[patientId];
        
        // Check if patient has uploads with valid modalities
        let hasValidScans = false;
        if (patient.uploads) {
          Object.keys(patient.uploads).forEach((scanKey) => {
            const scan = patient.uploads[scanKey];
            const rawModality = scan.Modality || scan.modality || scan.MODALITY || scan.StudyType || "";
            const mappedModality = mapModality(rawModality);
            if (mappedModality) {
              hasValidScans = true;
            }
          });
        }
        
        // Only count patients with valid scans
        if (hasValidScans) {
          const status = (patient.Report_Status || "").toLowerCase();
          filteredReportStats.total++;
          if (status === "final" || status === "finalized") {
            filteredReportStats.final++;
          } else if (status === "read") {
            filteredReportStats.read++;
          } else {
            filteredReportStats.pending++;
          }
        }
      });
    } else {
      // For other filters, only count patients who are in filteredPatientIds (have valid scans)
      filteredPatientIds.forEach((patientId) => {
        const patient = patientsData[patientId];
        if (patient) {
          const status = (patient.Report_Status || "").toLowerCase();
          filteredReportStats.total++;
          if (status === "final" || status === "finalized") {
            filteredReportStats.final++;
          } else if (status === "read") {
            filteredReportStats.read++;
          } else {
            filteredReportStats.pending++;
          }
        }
      });
    }
    
    // Process AI Stats from Postpaid data
    if (postpaidData && Object.keys(postpaidData).length > 0) {
      // Iterate through each month (OCT 2025, SEP 2025, etc.)
      Object.keys(postpaidData).forEach((monthKey) => {
        const monthData = postpaidData[monthKey];
        
        // Check if monthData is an array
        if (Array.isArray(monthData)) {
          monthData.forEach((aiRun, index) => {
            // Check if AI run has required fields
            if (aiRun.AIModelName && aiRun.Timestamp) {
              const aiTimestamp = parseTimestamp(aiRun.Timestamp);
              let isAIInRange = false;
              
              if (filter === "all") {
                isAIInRange = true;
              } else if (filter === "today") {
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                isAIInRange = aiTimestamp >= startDate && aiTimestamp < tomorrow;
              } else if (filter === "yesterday") {
                isAIInRange = aiTimestamp >= startDate && aiTimestamp < endDate;
              } else if (filter === "custom") {
                // Custom date range - include both start and end dates
                const rangeStart = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                const rangeEnd = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);
                isAIInRange = aiTimestamp >= rangeStart && aiTimestamp <= rangeEnd;
              } else {
                // week, thisMonth, or pastMonth
                if (filter === "pastMonth") {
                  isAIInRange = aiTimestamp >= startDate && aiTimestamp <= endDate;
                } else {
                  isAIInRange = aiTimestamp >= startDate;
                }
              }
              
              if (isAIInRange) {
                filteredAIStats.totalRuns++;
                const modelName = aiRun.AIModelName || "Unknown";
                filteredAIStats.byModel[modelName] = (filteredAIStats.byModel[modelName] || 0) + 1;
                
                // Add to recent runs for display
                filteredAIStats.recentRuns.push({
                  id: `${monthKey}-${index}`,
                  model: modelName,
                  patientName: decryptPatientName(aiRun.PatientName),
                  date: aiRun.Timestamp,
                  status: aiRun.Status || "Completed"
                });
              }
            }
          });
        }
      });
      
      // Sort recent runs by timestamp (newest first) and limit to 10
      filteredAIStats.recentRuns.sort((a, b) => {
        const timeA = parseTimestamp(a.timestamp);
        const timeB = parseTimestamp(b.timestamp);
        return timeB - timeA;
      }).slice(0, 10);
    }
    
    // Create modality breakdown
    const modalityBreakdown = [
      { name: "X-ray", value: modalityCounts.XRAY, color: "#0088FE" },
      { name: "CT",    value: modalityCounts.CT,   color: "#00C49F" },
      { name: "MRI",   value: modalityCounts.MRI,  color: "#FFBB28" },
    ].filter((item) => item.value > 0);

    return {
      stats: {
        totalUploads,
        ctUploads: modalityCounts.CT,
        xrayUploads: modalityCounts.XRAY,
        mriUploads: modalityCounts.MRI,
        todayUploads: totalUploads,
        yesterdayUploads: 0,
        weekUploads: totalUploads,
        monthUploads: totalUploads,
        totalPatients: filteredReportStats.total,
        totalDoctors: stats.totalDoctors,
        totalTechnicians: stats.totalTechnicians,
      },
      reportStats: filteredReportStats,
      aiStats: filteredAIStats,
      modalityBreakdown,
    };
  };

  const chartColors = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loader-container">
          <div className="spinner"></div>
          <div className="loading-text">
            <h2>Please Wait</h2>
            <p>Loading Dashboard Data...</p>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getFilterLabel = (filter) => {
    const labels = {
      all: "All Time",
      today: "Today",
      week: "This Week",
      thisMonth: "This Month",
      pastMonth: "Previous Month",
      custom: "Custom"
    };
    return labels[filter] || "All Time";
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>Dashboard Overview</h1>
        <div className="date-filter-container">
          <div className="date-filter-dropdown">
            <Calendar className="calendar-icon" size={20} />
            {timeFilter === "custom" && customStartDate && customEndDate ? (
              <div className="custom-date-display">
                <span className="selected-date-range">
                  {customStartDate.toLocaleDateString('en-GB')} - {customEndDate.toLocaleDateString('en-GB')}
                </span>
                <button 
                  className="edit-dates-btn"
                  onClick={() => {
                    setShowDatePicker(!showDatePicker);
                    setDateRangeError(""); // Clear error when opening/closing
                  }}
                  title="Edit date range"
                >
                  <Calendar size={16} />
                </button>
              </div>
            ) : (
              <>
                <select
                  className="date-filter-select"
                  value={timeFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "custom") {
                      // Reset dates to allow fresh selection
                      setCustomStartDate(null);
                      setCustomEndDate(null);
                      setDateRangeError("");
                      setShowDatePicker(true);
                    } else {
                      setShowDatePicker(false);
                      handleFilterChange(value);
                    }
                    setTimeFilter(value);
                  }}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  {/* <option value="week">This Week</option> */}
                  <option value="thisMonth">This Month</option>
                  <option value="pastMonth">Previous Month</option>
                  <option value="custom">Custom</option>
                </select>
                <ChevronDown className="dropdown-icon" size={18} />
              </>
            )}
          </div>
          
          {showDatePicker && timeFilter === "custom" && (
            <div className="custom-date-picker">
              <DatePicker
                selected={customStartDate}
                onChange={(date) => {
                  setCustomStartDate(date);
                  setDateRangeError(""); // Clear error
                  
                  // Validate: if end date is before new start date, show error
                  if (customEndDate && date > customEndDate) {
                    setDateRangeError("Start date cannot be after end date");
                    return;
                  }
                  
                  // Close picker if both dates are now selected
                  if (date && customEndDate) {
                    setShowDatePicker(false);
                  }
                }}
                selectsStart
                startDate={customStartDate}
                endDate={customEndDate}
                placeholderText=""
                className="date-input"
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={new Date()}
              />
              <span className="date-separator">to</span>
              <DatePicker
                selected={customEndDate}
                onChange={(date) => {
                  setDateRangeError(""); // Clear error
                  
                  // Validate: end date cannot be before start date
                  if (customStartDate && date < customStartDate) {
                    setDateRangeError("End date cannot be before start date");
                    return;
                  }
                  
                  setCustomEndDate(date);
                  
                  // Close picker if both dates are now selected
                  if (customStartDate && date) {
                    setShowDatePicker(false);
                  }
                }}
                selectsEnd
                startDate={customStartDate}
                endDate={customEndDate}
                minDate={customStartDate || new Date(new Date().getFullYear() - 10, 0, 1)}
                maxDate={new Date()}
                placeholderText=""
                className="date-input"
                dateFormat="dd/MM/yyyy"
              />
              {dateRangeError && (
                <div style={{ 
                  color: '#e74c3c', 
                  fontSize: '12px', 
                  marginTop: '8px',
                  fontWeight: '500'
                }}>
                  ⚠️ {dateRangeError}
                </div>
              )}
              <button
                className="clear-date-btn"
                onClick={() => {
                  setCustomStartDate(null);
                  setCustomEndDate(null);
                  setDateRangeError(""); // Clear error
                  setShowDatePicker(false);
                  setTimeFilter("all");
                  handleFilterChange("all");
                }}
                title="Clear dates"
              >
                <X size={18} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-cards-grid">
        <div className="stat-card stat-card-primary">
          <div className="stat-icon-wrapper stat-icon-primary">
            <BarChart3 size={32} />
          </div>
          <div className="stat-content">
            <h3>Total Scans</h3>
            <p className="stat-number">{reportStats.total}</p>
          </div>
        </div>

        <div className="stat-card stat-card-warning">
          <div className="stat-icon-wrapper stat-icon-warning">
            <Zap size={32} />
          </div>
          <div className="stat-content">
            <h3>AI Analysis</h3>
            <p className="stat-number">{aiStats.totalRuns}</p>
          </div>
        </div>
      </div>

      {/* Modality Statistics Cards */}
      <div className="modality-stats-grid">
        {!showMriCt && (
          <div className="modality-stat-card xray-card">
            <div className="modality-header">
              <Activity className="modality-icon-svg" size={28} />
              <h3>X-ray</h3>
            </div>
            <p className="modality-count">{stats.xrayUploads}</p>
          </div>
        )}

        {showMriCt && (
          <div className="modality-stat-card ct-card">
            <div className="modality-header">
              <BarChart3 className="modality-icon-svg" size={28} />
              <h3>CT</h3>
            </div>
            <p className="modality-count">{stats.ctUploads}</p>
          </div>
        )}

        {showMriCt && (
          <div className="modality-stat-card mri-card">
            <div className="modality-header">
              <TrendingUp className="modality-icon-svg" size={28} />
              <h3>MRI</h3>
            </div>
            <p className="modality-count">{stats.mriUploads}</p>
          </div>
        )}
      </div>

      {/* Charts Section */}
      <div className="charts-grid">
        {/* Report Status Pie Chart */}
        <div className="chart-card">
          <h3>Report Status</h3>
          {reportStats.total > 0 ? (
            <div style={{ position: 'relative', paddingTop: '50px' }}>
              {/* Legend Box - Top Right */}
              <div style={{ 
                position: 'absolute',
                top: '0',
                right: '0',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                padding: '16px 20px',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: '2px solid #e0e0e0',
                minWidth: '130px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                zIndex: 10
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ 
                    width: '18px', 
                    height: '18px', 
                    backgroundColor: '#00C49F',
                    borderRadius: '3px'
                  }}></div>
                  <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '15px' }}>Final</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ 
                    width: '18px', 
                    height: '18px', 
                    backgroundColor: '#0088FE',
                    borderRadius: '3px'
                  }}></div>
                  <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '15px' }}>Read</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ 
                    width: '18px', 
                    height: '18px', 
                    backgroundColor: '#FFBB28',
                    borderRadius: '3px'
                  }}></div>
                  <span style={{ color: '#2c3e50', fontWeight: '600', fontSize: '15px' }}>Pending</span>
                </div>
              </div>

              {/* Pie Chart */}
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: "Final", value: reportStats.final, color: "#00C49F" },
                      { name: "Read", value: reportStats.read, color: "#0088FE" },
                      { name: "Pending", value: reportStats.pending, color: "#FFBB28" },
                    ].filter((item) => item.value > 0)}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ cx, cy, midAngle, innerRadius, outerRadius, name, value }) => {
                      const RADIAN = Math.PI / 180;
                      const radius = outerRadius + 25;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#333"
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          style={{ fontSize: '14px', fontWeight: '600' }}
                        >
                          {`${name}: ${value}`}
                        </text>
                      );
                    }}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[
                      { name: "Final", value: reportStats.final, color: "#00C49F" },
                      { name: "Read", value: reportStats.read, color: "#0088FE" },
                      { name: "Pending", value: reportStats.pending, color: "#FFBB28" },
                    ]
                      .filter((item) => item.value > 0)
                      .map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="no-data-message">
              <p>No report data available.</p>
            </div>
          )}
        </div>

        {/* Upload Trends Line Chart - COMMENTED OUT */}
        {/* <div className="chart-card chart-card-wide">
          <h3>Upload Trends (Last 6 Months)</h3>
          {uploadTrends.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={uploadTrends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="uploads"
                  stroke="#8884d8"
                  strokeWidth={2}
                  dot={{ r: 5 }}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="no-data-message">
              <p>No trend data available.</p>
            </div>
          )}
        </div> */}

        {/* AI Model Usage Line Chart */}
        <div className="chart-card ai-model-chart">
          <div className="chart-header">
            <h3>AI Model Usage</h3>
          </div>
          
          {aiUsageTrends && aiUsageTrends.length > 0 ? (
              (() => {
                // Extract all unique model names from trends data
                const modelNames = new Set();
                aiUsageTrends.forEach(trend => {
                  Object.keys(trend).forEach(key => {
                    if (key !== 'time' && key !== 'total') {
                      modelNames.add(key);
                    }
                  });
                });

                // Transform data for ModelUsageRechart
                const chartData = Array.from(modelNames).map(modelName => ({
                  model: modelName.replace(/_/g, " ").replace(/-/g, " "),
                  runs: aiUsageTrends.map(trend => trend[modelName] === undefined ? null : trend[modelName])
                }));

                const xLabels = aiUsageTrends.map(trend => trend.time);

                return (
                  <ModelUsageRechart 
                    data={chartData}
                    xLabels={xLabels}
                    height={380} 
                  />
                );
              })()
            ) : (
              <div className="no-data-message">
                <p>No AI processing data available for this period.</p>
              </div>
            )}
        </div>
      </div>

      {/* Recent Activity Tables */}
      <div className="activity-section">
        <div className="activity-card" style={{ maxWidth: '800px', margin: '0' }}>
          <h3>📋Report Summary</h3>
          {reportsTableData.length > 0 ? (
            <>
              {/* Count information */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                alignItems: 'center',
                marginTop: '10px',
                marginBottom: '8px'
              }}>
                {/* <div style={{ fontSize: '13px', color: '#6c757d' }}>
                  Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalReportsCount)} to {Math.min(currentPage * itemsPerPage, totalReportsCount)} of {totalReportsCount}
                </div> */}
              </div>

              {loadingReports ? (
                <div style={{ 
                  padding: '30px 20px', 
                  textAlign: 'center',
                  color: '#6c757d'
                }}>
                  <div className="spinner" style={{ margin: '0 auto' }}></div>
                  <p style={{ marginTop: '10px' }}>Loading reports...</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ 
                    width: '100%', 
                    borderCollapse: 'collapse',
                    fontSize: '14px'
                  }}>
                    <thead>
                      <tr style={{ 
                        backgroundColor: '#f8f9fa',
                        borderBottom: '2px solid #dee2e6'
                      }}>
                        <th style={{ 
                          padding: '12px 16px', 
                          textAlign: 'left',
                          fontWeight: '600',
                          color: '#495057',
                          width: '65%',
                          cursor: 'default',
                          userSelect: 'none',
                          backgroundColor: '#f8f9fa',
                          transition: 'none',
                          pointerEvents: 'none'
                        }}>Doctor Name</th>
                        <th style={{ 
                          padding: '12px 16px', 
                          textAlign: 'center',
                          fontWeight: '600',
                          color: '#495057',
                          width: '35%',
                          cursor: 'default',
                          userSelect: 'none',
                          backgroundColor: '#f8f9fa',
                          transition: 'none',
                          pointerEvents: 'none'
                        }}>Reports Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportsTableData.map((report, index) => (
                      <tr key={index} style={{ 
                        borderBottom: '1px solid #e9ecef'
                      }}
                      >
                        <td style={{ 
                          padding: '12px 16px',
                          color: '#0088FE',
                          fontWeight: '600',
                          textAlign: 'left',
                          fontSize: '15px'
                        }}>{report.doctorName}</td>
                        <td style={{ 
                          padding: '12px 16px',
                          color: '#155724',
                          fontWeight: '700',
                          textAlign: 'center',
                          fontSize: '16px'
                        }}>{report.reportCount}</td>
                      </tr>
                    ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination Controls */}
              {Math.ceil(totalReportsCount / itemsPerPage) > 1 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  gap: '6px',
                  marginTop: '15px',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                      color: currentPage === 1 ? '#6c757d' : '#495057',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Previous
                  </button>

                  {(() => {
                    const totalPages = Math.ceil(totalReportsCount / itemsPerPage);
                    const pages = [];
                    const maxPagesToShow = 5;
                    
                    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
                    
                    if (endPage - startPage < maxPagesToShow - 1) {
                      startPage = Math.max(1, endPage - maxPagesToShow + 1);
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          style={{
                            padding: '6px 10px',
                            fontSize: '13px',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            backgroundColor: currentPage === i ? '#0088FE' : 'white',
                            color: currentPage === i ? 'white' : '#495057',
                            cursor: 'pointer',
                            fontWeight: currentPage === i ? '600' : '500',
                            minWidth: '35px'
                          }}
                        >
                          {i}
                        </button>
                      );
                    }
                    return pages;
                  })()}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(totalReportsCount / itemsPerPage)))}
                    disabled={currentPage === Math.ceil(totalReportsCount / itemsPerPage)}
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      backgroundColor: currentPage === Math.ceil(totalReportsCount / itemsPerPage) ? '#f8f9fa' : 'white',
                      color: currentPage === Math.ceil(totalReportsCount / itemsPerPage) ? '#6c757d' : '#495057',
                      cursor: currentPage === Math.ceil(totalReportsCount / itemsPerPage) ? 'not-allowed' : 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ) : (
            <div style={{ 
              padding: '40px 20px', 
              textAlign: 'center',
              color: '#6c757d'
            }}>
              <p>No completed reports found for this period.</p>
            </div>
          )}
        </div>

        {/* Study Category Table */}
        <div className="activity-card" style={{ maxWidth: '800px', margin: '0' }}>
          <h3>📊 Study Category</h3>
          {studyCategoryData.length > 0 ? (
            <div style={{ overflowX: 'auto', marginTop: '15px' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ 
                    backgroundColor: '#f8f9fa',
                    borderBottom: '2px solid #dee2e6'
                  }}>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'left',
                      fontWeight: '600',
                      color: '#495057',
                      width: '65%',
                      cursor: 'default',
                      userSelect: 'none',
                      backgroundColor: '#f8f9fa',
                      transition: 'none',
                      pointerEvents: 'none'
                    }}>Category</th>
                    <th style={{ 
                      padding: '12px 16px', 
                      textAlign: 'center',
                      fontWeight: '600',
                      color: '#495057',
                      width: '35%',
                      cursor: 'default',
                      userSelect: 'none',
                      backgroundColor: '#f8f9fa',
                      transition: 'none',
                      pointerEvents: 'none'
                    }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {studyCategoryData.map((item, index) => (
                    <tr key={index} style={{ 
                      borderBottom: '1px solid #e9ecef'
                    }}>
                      <td style={{ 
                        padding: '12px 16px',
                        color: '#0088FE',
                        fontWeight: '600',
                        textAlign: 'left',
                        fontSize: '15px'
                      }}>{item.category}</td>
                      <td style={{ 
                        padding: '12px 16px',
                        color: '#155724',
                        fontWeight: '700',
                        textAlign: 'center',
                        fontSize: '16px'
                      }}>{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ 
              padding: '40px 20px', 
              textAlign: 'center',
              color: '#6c757d'
            }}>
              <p>No study data available for this period.</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Dashboard;
